import React, { ReactNode } from 'react';
import { Separator } from '@/components/ui/separator';
import GlassIcon from '@/components/ui/GlassIcon';
import GlassPill from '@/components/ui/GlassPill';
import { LucideIcon, X, ChevronDown } from 'lucide-react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

interface SidebarHeaderProps {
  title: string;
  icon: LucideIcon;
  variant?: 'purple' | 'blue' | 'indigo' | 'green' | 'amber';
  onClear?: () => void;
  clearButtonLabel?: string;
  // Accordion props
  isAccordion?: boolean;
  isExpanded?: boolean;
  onToggle?: () => void;
  showChevron?: boolean;
}

const SidebarHeader: React.FC<SidebarHeaderProps> = ({
  title,
  icon,
  variant = 'purple',
  onClear,
  clearButtonLabel = 'Clear',
  isAccordion = false,
  isExpanded = true,
  onToggle,
  showChevron = true
}) => {
  // Get variant-specific gradient classes
  const getGradientClasses = () => {
    switch (variant) {
      case 'blue':
        return 'from-blue-500 to-sky-400 dark:from-blue-400 dark:to-sky-300';
      case 'green':
        return 'from-green-500 to-emerald-400 dark:from-green-400 dark:to-emerald-300';
      case 'amber':
        return 'from-amber-500 to-yellow-400 dark:from-amber-400 dark:to-yellow-300';
      case 'indigo':
        return 'from-indigo-500 to-blue-400 dark:from-indigo-400 dark:to-blue-300';
      case 'purple':
      default:
        return 'from-purple-500 to-blue-400 dark:from-purple-400 dark:to-blue-300';
    }
  };
  const HeaderContent = () => (
    <>
      {/* Header with icon and title */}
      <div className="flex items-center justify-between relative">
        <div className="flex items-center gap-3">
          <motion.div
            className="group-hover:scale-110 transition-transform duration-200 ease-out"
          >
            <GlassIcon icon={icon} variant={variant} size={18} className="w-7 h-7 md:w-9 md:h-9" />
          </motion.div>
          <motion.h2 className={cn(
            `text-sm md:text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r ${getGradientClasses()} group-hover:scale-105 transition-transform duration-200 ease-out`,
            isAccordion && "hidden md:block" // Hide on mobile when accordion, show on desktop
          )}>
            {title}
          </motion.h2>
        </div>
        
        {/* Mobile: Centered title when accordion */}
        {isAccordion && (
          <motion.h2 className={`md:hidden absolute left-1/2 transform -translate-x-1/2 text-sm font-bold text-transparent bg-clip-text bg-gradient-to-r ${getGradientClasses()} group-hover:scale-105 transition-transform duration-200 ease-out`}>
            {title}
          </motion.h2>
        )}
        
        <div className="flex items-center gap-2">
          {onClear && (
            <motion.div 
              onClick={onClear} 
              className="cursor-pointer group-hover:scale-105 transition-transform duration-200"
              whileTap={{ scale: 0.95 }}
            >
              <GlassPill 
                type=""
                variant="purple"
                className="text-xs py-0.5 px-2"
              >
                <X size={12} />
                {clearButtonLabel}
              </GlassPill>
            </motion.div>
          )}
          
          {isAccordion && showChevron && (
            <div className="p-1 rounded-md">
              <motion.div
                initial={false}
                animate="default"
                variants={{
                  default: { rotate: isExpanded ? 180 : 0, scale: 1 },
                  hover: { rotate: -90, scale: 1.15 }
                }}
                className="transform-gpu"
                transition={{ 
                  type: "spring",
                  stiffness: 300,
                  damping: 22,
                  mass: 0.8
                }}
              >
                <ChevronDown className="h-4 w-4 md:h-5 md:w-5 text-gray-500" />
              </motion.div>
            </div>
          )}
        </div>
      </div>
      {/* Separator removed */}
    </>
  );

  // If it's an accordion, wrap in a clickable area with balanced padding
  if (isAccordion) {
    return (
      <motion.button
        onClick={onToggle}
        className="w-full text-left p-2 md:px-6 md:py-5 transition-all duration-200 group"
        aria-expanded={isExpanded}
        whileHover="hover"
        initial="default"
        key={`accordion-${isExpanded}`}
      >
        <HeaderContent />
      </motion.button>
    );
  }

  // For non-accordion mode, render with normal spacing
  return (
    <div>
      <HeaderContent />
    </div>
  );
};

SidebarHeader.displayName = 'SidebarHeader';

export default SidebarHeader;
